// =============================================================================
// Real HTTP backend for the V3 (Priority Spel 3.0) API.
//
// Implements the SAME method names and return shapes as ../mock/mockBackend so the store
// (useGameStore) is unchanged when we flip the swap point in ../apiClient.js. The Laravel API
// is additive `v3/*` at the ROOT (apiPrefix is '', so there is no /api segment). The API is
// uuid-addressed like the old game: authenticate returns a uuid, which we persist and send on
// every subsequent call.
// =============================================================================

const BASE = String(import.meta.env.VITE_API_ENDPOINT || '/').replace(/\/+$/, '') + '/'
const UUID_KEY = 'v3_uuid'

// Security audit V3-09: the uuid is the player's bearer token, so it lives in sessionStorage. It
// survives a refresh but not closing the webview; re-entry comes with a fresh VZ token. A uuid an
// older build left in localStorage is moved over once and removed there.
let uuid = null
try {
  uuid = sessionStorage.getItem(UUID_KEY) || localStorage.getItem(UUID_KEY) || null
  if (uuid) sessionStorage.setItem(UUID_KEY, uuid)
  localStorage.removeItem(UUID_KEY)
} catch {
  /* private mode / storage blocked — fall back to in-memory only */
}

function setUuid(value) {
  uuid = value || null
  try {
    if (value) sessionStorage.setItem(UUID_KEY, value)
    else sessionStorage.removeItem(UUID_KEY)
  } catch {
    /* ignore storage failures */
  }
}

/** POST a JSON body to a V3 endpoint and return the parsed JSON. */
async function post(path, body = {}) {
  let res
  try {
    res = await fetch(BASE + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new Error('network_error')
  }

  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }

  // Game-logic errors come back as HTTP 200 with { error, state } (the store handles those,
  // exactly like the mock). Only real transport/auth failures are thrown.
  if (!res.ok) {
    const err = new Error((data && data.error) || `http_${res.status}`)
    err.status = res.status
    err.data = data // e.g. { retryAfter, state } on a 429 (rate-limited SMS code request)
    throw err
  }

  return data ?? {}
}

/** Attach the persisted uuid to a request body (the server keys everything on it). */
function withUuid(body = {}) {
  return uuid ? { uuid, ...body } : body
}

export const httpBackend = {
  // Entry: a JWE token (?orderKey=…) on first arrival, or the persisted uuid afterwards.
  async authenticate({ token, uuid: forcedUuid } = {}) {
    const id = forcedUuid || uuid
    const body = token ? { token } : id ? { uuid: id } : {}
    const res = await post('v3/a', body)
    if (res.uuid) setUuid(res.uuid)
    return res
  },

  async getState() {
    return post('v3/state', withUuid())
  },

  async markIntroSeen() {
    return post('v3/intro-seen', withUuid())
  },

  async spin() {
    return post('v3/spin', withUuid())
  },

  async finishGame({ gameId, score = 0, attempts, completed = true } = {}) {
    return post('v3/finish', withUuid({ gameId, score, attempts, completed }))
  },

  async quitGame({ gameId } = {}) {
    return post('v3/quit', withUuid({ gameId }))
  },

  async getShop() {
    return post('v3/shop', withUuid())
  },

  async confirmShop({ bets = {} } = {}) {
    return post('v3/shop/confirm', withUuid({ bets }))
  },

  async requestMsisdnCode({ msisdn } = {}) {
    return post('v3/msisdn/request', withUuid({ msisdn }))
  },

  async verifyMsisdn({ code } = {}) {
    return post('v3/msisdn/verify', withUuid({ code }))
  },

  async setCommunicationOptin({ accept } = {}) {
    return post('v3/optin/communication', withUuid({ accept }))
  },

  async setReminderOptin({ accept } = {}) {
    return post('v3/optin/reminder', withUuid({ accept }))
  },

  async validateEmail({ email } = {}) {
    return post('v3/email/validate', withUuid({ email }))
  },

  // Dev/test reset helpers are mock-only (the real resets run via artisan: v3:weekly-reset /
  // v3:cycle-reset). Against the real backend these just re-read state so any dev button stays
  // harmless instead of throwing.
  async _devWeeklyReset() {
    return this.getState()
  },
  async _devCycleReset() {
    return this.getState()
  },
  async _devReset() {
    return this.getState()
  },
}
